import {BigCalendarEvent, BigCalendarUser} from 'platform/big-calendar';

import {GetDataByDataQueryApiResponse} from '@dms/api/core';

import {Nullish, parseDate} from 'shared/web';

import {getIconKeyByType} from '../../../../../utils/getIconKeyByType';
import {getUserColorVariants} from './getUserColorVariants';
import {parseDueTime} from './parseDueTime';

/**
 * Converts data grid data to BigCalendar events with comprehensive task information
 * @param dataGridData - Data grid response containing task rows
 * @param selectedUsers - Array of selected users for color assignment
 * @returns Array of BigCalendarEvent objects with full task metadata
 */
export function getBigCalendarDataFromGrid(
  dataGridData: GetDataByDataQueryApiResponse | Nullish,
  selectedUsers: BigCalendarUser[] | Nullish
) {
  return dataGridData?.rows?.reduce<BigCalendarEvent[]>((events, row) => {
    const dueDate: string | null = row.cells.dueDate.value;
    const dueTime: string | null = row.cells.dueTime.value;

    const assignedUserId = row.cells.assignedTo?.value?.id;
    const reportedByUserId = row.cells.reportedBy?.value?.id;
    const createdByUserId = row.cells.createdBy?.value?.id;
    const description = row.cells.description?.value;

    const baseDate = parseDate(dueDate);

    const userIndex = selectedUsers?.findIndex((user) => user.id === assignedUserId);

    const userColorVariants = getUserColorVariants(userIndex);

    if (!baseDate) {
      return events;
    }

    const commonEventProperties = {
      id: row.id,
      title: row.cells.title.value,
      color: userColorVariants[row.cells?.priority?.value?.key],
      icon: getIconKeyByType(row.cells?.type?.value?.key),
      alternative: row.cells.state.value?.key === 'DONE',
      reportedBy: reportedByUserId,
      assignedTo: assignedUserId,
      createdBy: createdByUserId,
      priority: row.cells?.priority?.value?.key,
      type: row.cells?.type?.value?.key,
      state: row.cells.state.value?.key,
      description,
    };

    if (!dueTime) {
      events.push({
        ...commonEventProperties,
        start: parseDate(baseDate.setHours(0, 0, 0)),
        end: parseDate(baseDate.setHours(0, 0, 0)),
        isAllDay: true,
      });
      return events;
    }

    const {start, end} = parseDueTime(dueTime, baseDate);

    events.push({
      ...commonEventProperties,
      start,
      end,
    });

    return events;
  }, []);
}
